import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth } from '../services/api';
import { getAccessToken, setAccessToken, clearAccessToken } from '../services/auth';
import { getDeviceId } from '../services/socialLogin';
import { track, identify, reset as resetAnalytics } from '../instrument';
import socket from '../services/socket';

const AuthContext = createContext(null);

// Соединение с игрой запоминает игрока в момент подключения: сервер берёт userId из
// ключа доступа один раз, при рукопожатии. Если человек сменил аккаунт, а соединение
// осталось прежним, сервер продолжает считать его прежним игроком — свои же партии
// становятся «чужими» («Ты не игрок этой партии», поймано 01.09.2026 у Ovik).
// Поэтому при каждой смене аккаунта соединение пересоздаём: оно подключится заново
// и представится новым ключом.
const resetSocketIdentity = (reconnect) => {
  try {
    const wasConnected = socket.connected;
    if (wasConnected) socket.disconnect();
    if (wasConnected && reconnect) socket.connect();
  } catch { /* соединения может не быть вовсе — это не ошибка */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // При загрузке: если в sessionStorage есть токен — пробуем /me.
  // Если токена нет — пробуем refresh (вдруг cookie жива).
  useEffect(() => {
    const init = async () => {
      const token = getAccessToken();
      try {
        if (!token) {
          await auth.refresh();
        }
        const { user } = await auth.me();
        setUser(user);
        identify(user.id || user._id, { username: user.username });
      } catch {
        // DEV быстрый старт: на localhost пробуем авто-вход без формы.
        // Сервер пустит ТОЛЬКО при DEV_AUTOLOGIN=1 (npm run dev:log); иначе — обычная форма входа.
        // НО: если юзер вышел ВРУЧНУЮ (флаг), авто-вход НЕ делаем — иначе выход не «прилипает».
        const manualLogout = typeof localStorage !== 'undefined'
          && localStorage.getItem('blotme_no_autologin') === '1';
        const isLocal = typeof window !== 'undefined'
          && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
        if (isLocal && !manualLogout) {
          try {
            const { accessToken, user } = await auth.devLogin();
            setAccessToken(accessToken);
            setUser(user);
            identify(user.id || user._id, { username: user.username });
            return;   // finally всё равно снимет loading
          } catch {
            // dev-login недоступен → показываем обычный вход
          }
        }
        clearAccessToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Снять флаг «вышел вручную» — при любом реальном входе.
  const clearManualLogout = () => {
    try { localStorage.removeItem('blotme_no_autologin'); } catch {}
  };

  const login = useCallback(async (email, password) => {
    const { accessToken, user } = await auth.login({ email, password });
    clearManualLogout();
    setAccessToken(accessToken);
    resetSocketIdentity(true);
    setUser(user);
    identify(user.id || user._id, { username: user.username });
    track('login');
    return user;
  }, []);

  const register = useCallback(async (data) => {
    const { accessToken, user } = await auth.register(data);
    clearManualLogout();
    setAccessToken(accessToken);
    resetSocketIdentity(true);
    setUser(user);
    identify(user.id || user._id, { username: user.username });
    track('signup');
    return user;
  }, []);

  // Общая «посадка» юзера после успешного ответа сервера (накопитель повторов).
  const applyAuth = useCallback((accessToken, user, evt) => {
    clearManualLogout();
    setAccessToken(accessToken);
    resetSocketIdentity(true);
    setUser(user);
    identify(user.id || user._id, { username: user.username });
    track(evt);
    return user;
  }, []);

  const loginWithGoogle = useCallback(async (googleAccessToken) => {
    const { accessToken, user } = await auth.google({ accessToken: googleAccessToken });
    return applyAuth(accessToken, user, 'login_google');
  }, [applyAuth]);

  const loginWithFacebook = useCallback(async (fbAccessToken) => {
    const { accessToken, user } = await auth.facebook(fbAccessToken);
    return applyAuth(accessToken, user, 'login_facebook');
  }, [applyAuth]);

  const loginAsGuest = useCallback(async () => {
    const { accessToken, user } = await auth.guest(getDeviceId());
    return applyAuth(accessToken, user, 'login_guest');
  }, [applyAuth]);

  const logout = useCallback(async () => {
    try { await auth.logout(); } catch {}
    // Помечаем явный выход — чтобы DEV-автологин (npm run dev:log) не впустил снова после рефреша.
    try { localStorage.setItem('blotme_no_autologin', '1'); } catch {}
    clearAccessToken();
    resetSocketIdentity(false);   // выходим совсем — соединение не нужно
    setUser(null);
    track('logout');
    resetAnalytics();
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, setUser,
      loginWithGoogle, loginWithFacebook, loginAsGuest,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
